import { useEffect } from 'react'
import type { ComponentType, ReactNode } from 'react'
import { cn } from '@teach/ui'
import { BAR_CLASS, useRubberBar } from './useRubberBar'
import { useScrollFade } from './useScrollFade'

interface Tab<T extends string> {
  id: T
  label: string
  icon: ComponentType<{ className?: string }>
  /** Что показать справа от подписи: точка «требует внимания», метка «Скоро». */
  extra?: ReactNode
}

interface Props<T extends string> {
  tabs: Tab<T>[]
  value: T
  onChange: (id: T) => void
}

/** Вкладки-кнопки с резиновым подчёркиванием, как переключатель разделов в профиле. */
export const UnderlineTabs = <T extends string>({ tabs, value, onChange }: Props<T>) => {
  const active = Math.max(
    0,
    tabs.findIndex((tab) => tab.id === value),
  )
  const { navRef, itemRef, barStyle } = useRubberBar(active)
  const fade = useScrollFade(navRef)
  // На узком экране вкладки прокручиваются: выбранная (в т.ч. переход к замечанию
  // публикации) не должна оставаться за краем.
  useEffect(() => {
    navRef.current?.children[active]?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' })
  }, [active, navRef])
  return (
    <nav
      ref={navRef}
      style={fade}
      className="relative flex gap-1 overflow-x-auto border-b border-border [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {tabs.map(({ id, label, icon: Icon, extra }, index) => (
        <button
          key={id}
          type="button"
          ref={itemRef(index)}
          aria-current={id === value ? 'page' : undefined}
          onClick={() => onChange(id)}
          className={cn(
            'flex items-center gap-2 whitespace-nowrap px-3 py-2.5 text-sm transition-colors',
            id === value
              ? 'font-medium text-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Icon className="size-4" />
          {label}
          {extra}
        </button>
      ))}
      {barStyle ? <span aria-hidden="true" className={BAR_CLASS} style={barStyle} /> : null}
    </nav>
  )
}
